import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import "./ConfirmDialog.scss";

// Reusable custom interactive confirmation dialog with clean modal header, footer,
// close button, formatted body, and type verification.
const ConfirmDialog = ({
  open,
  onClose,
  title,
  content,
  buttonText = "Yes",
  additionalButtonText = "No",
  onButtonClick,
  onAdditionalButtonClick,
  severity = "info", // "info" | "warning" | "error"
  confirmText, // If passed, require typing this exact text to confirm
}) => {
  const [confirmInput, setConfirmInput] = useState("");
  const [shakeInput, setShakeInput] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset state when the dialog closes. Adjusting it here (during render,
  // React's documented escape hatch for this exact case) instead of in an
  // effect clears the input before the closed state ever paints, without
  // remounting <Dialog> via `key` — which would kill MUI's exit-transition.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setConfirmInput("");
      setShakeInput(false);
    }
  }

  const isMatched = !confirmText || confirmInput.trim() === confirmText;

  const handlePrimaryClick = () => {
    if (confirmText && !isMatched) {
      setShakeInput(true);
      setTimeout(() => setShakeInput(false), 400);
      return;
    }
    onClose();
    if (onButtonClick) {
      onButtonClick();
    }
  };

  const handleSecondaryClick = () => {
    if (onAdditionalButtonClick) {
      onAdditionalButtonClick();
    } else {
      onClose();
    }
  };

  const getSeverityIcon = () => {
    switch (severity) {
      case "error":
        return <DeleteForeverRoundedIcon />;
      case "warning":
        return <WarningRoundedIcon />;
      case "info":
      default:
        return <InfoRoundedIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        className: "confirm-dialog-paper",
      }}
      aria-labelledby="confirm-dialog-title"
    >
      {/* Absolute Close Button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        className="confirm-dialog-close-btn"
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Modal Left-Aligned Header */}
      <div className="confirm-dialog-header">
        <div className={`confirm-dialog-icon severity-${severity}`}>
          {getSeverityIcon()}
        </div>
        {title && (
          <h2 id="confirm-dialog-title" className="confirm-dialog-title">
            {title}
          </h2>
        )}
      </div>

      {/* Modal Left-Aligned Body */}
      <DialogContent className="confirm-dialog-body">
        <div className="confirm-dialog-text">
          {content}
        </div>

        {confirmText && (
          <div className="confirm-verification-section">
            <p className="confirm-verification-instruction">
              To confirm, type <span className="confirm-word-highlight">{confirmText}</span> in the input field below:
            </p>
            <div className="confirm-input-container">
              <input
                type="text"
                placeholder={`Type "${confirmText}"`}
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                className={`confirm-verification-input ${
                  confirmInput.trim() === confirmText ? "matched" : ""
                } ${shakeInput ? "shake" : ""}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isMatched) {
                    handlePrimaryClick();
                  } else if (e.key === "Enter" && !isMatched) {
                    setShakeInput(true);
                    setTimeout(() => setShakeInput(false), 400);
                  }
                }}
              />
              {confirmInput.trim() === confirmText && (
                <div className="confirm-match-icon">
                  <CheckCircleRoundedIcon />
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      {/* Modal Right-Aligned Actions */}
      <DialogActions className="confirm-dialog-actions">
        <button
          type="button"
          onClick={handleSecondaryClick}
          className="action-btn btn-cancel"
        >
          {additionalButtonText}
        </button>
        <button
          type="button"
          onClick={handlePrimaryClick}
          disabled={!isMatched}
          className={`action-btn btn-confirm severity-${severity}`}
        >
          {buttonText}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;

